import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { QRCodeCanvas } from "qrcode.react";
import {
  X,
  Link2,
  User,
  Phone,
  IndianRupee,
  FileText,
  Copy,
  QrCode,
  MessageCircle,
  Loader2,
  Sparkles,
  Banknote,
  CreditCard,
} from "lucide-react";
import { BookingType } from "../../../../Data/package.dto";
import {
  addManualPaymentAPI,
  GenerateBookPackagePaymentAPI,
} from "../../../../Redux/ApiCalls/Dashboard/PackageAPI";
import { useAppSelector } from "../../../../Redux/Hooks";
import { showErrorToast, showSuccessToast } from "../../Utlis/Toast";
import { getWhiteLableObject } from "../../../../config/whitelable";
import { trackClarityEvent } from "../../../../analytics/ClarityAnalytics";
import { CLARITY_EVENTS } from "../../../../analytics/EventTypes";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  PrimaryBtn,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  Field,
  Label,
  Input,
  ErrorSpan,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

const PLATFORM_FEE = 10;
type PaymentProvider = "cashfree" | "razorpay";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 600;
  padding: 14px 16px;
  color: #171717;
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
    background: #fff;
  }
  &::placeholder {
    color: rgba(23, 23, 23, 0.35);
    font-weight: 500;
  }
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const ModeCard = styled.label<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px 14px;
  border-radius: 14px;
  border: 1px solid ${(p) => (p.$active ? "rgba(201, 77, 170, 0.35)" : "rgba(23, 23, 23, 0.1)")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, rgba(255, 201, 81, 0.12) 0%, rgba(206, 86, 176, 0.08) 100%)"
      : "#fafafa"};
  cursor: pointer;
  transition: all 0.15s ease;
  input { display: none; }
  &:hover { border-color: rgba(201, 77, 170, 0.25); }
`;

const ModeIcon = styled.div<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$active ? "rgba(201, 77, 170, 0.12)" : "rgba(23, 23, 23, 0.05)")};
  color: ${(p) => (p.$active ? "#c94daa" : "rgba(23, 23, 23, 0.5)")};
`;

const ModeLabel = styled.span<{ $active: boolean }>`
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => (p.$active ? "#c94daa" : "rgba(23, 23, 23, 0.65)")};
`;

const FeeCard = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.68);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FeeTotal = styled.div`
  margin-top: 6px;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const FeeHint = styled.span`
  font-size: 11px;
  color: rgba(23, 23, 23, 0.45);
`;

const LinkBox = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: #fafafa;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  word-break: break-all;
  margin-bottom: 12px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SecondaryBtn = styled.button`
  flex: 1;
  min-width: 100px;
  height: 44px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 12px;
  background: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.15s ease;
  &:hover { border-color: rgba(201, 77, 170, 0.25); color: #c94daa; }
`;

const WhatsAppBtn = styled.button`
  flex: 1;
  min-width: 100px;
  height: 44px;
  border: none;
  border-radius: 12px;
  background: #25d366;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.9; }
`;

const QrWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
  padding: 16px;
  border-radius: 16px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
`;

const extractBookingPaymentPayload = (rawResponse: any) => {
  if (rawResponse?.data && typeof rawResponse.data === "object") return rawResponse.data;
  return rawResponse;
};

const buildBookingReturnUrl = (domain: string) => {
  if (!domain) return window.location.origin;
  const normalizedDomain = domain.replace(/\/+$/, "");
  if (/^https?:\/\//i.test(normalizedDomain)) return `${normalizedDomain}/`;
  const protocol =
    normalizedDomain.includes("localhost") || normalizedDomain.startsWith("127.0.0.1")
      ? "http"
      : "https";
  return `${protocol}://${normalizedDomain}/`;
};

const GenerateBookPackagePaymentModal = ({
  isOpen,
  booking_data,
  onClose,
  refreshData,
}: {
  isOpen: boolean;
  booking_data: BookingType;
  onClose: () => void;
  refreshData: () => void;
}) => {
  const [formData, setFormData] = useState({
    customerName: booking_data?.customer_name ?? "",
    contact: "",
    amount: "",
    description: "",
    paymentMode: "upi",
  });
  const { user, business } = useAppSelector((state) => state.auth);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isCash, setIsCashPayment] = useState(false);

  const handlePaymentModeChange = (mode: string) => {
    if (mode === "cash") {
      setIsCashPayment(true);
      setFormData({ ...formData, paymentMode: "cash" });
    } else {
      setIsCashPayment(false);
      setFormData({ ...formData, paymentMode: mode });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const calculateFinalPrice = () => {
    const baseAmount = Number(formData.amount) || 0;
    if (baseAmount <= 0) return { customerPays: 0, razorpayFee: 0, gst: 0, baseAmount: 0 };
    const customerPays = baseAmount + PLATFORM_FEE;
    return { customerPays, razorpayFee: 0, gst: 0, baseAmount };
  };

  const { customerPays } = calculateFinalPrice();

  const resolvePaymentProvider = (): PaymentProvider => {
    const businessDetails = (business || {}) as Record<string, any>;
    const configuredProvider = String(
      businessDetails.payment_provider || businessDetails.preferred_payment_provider || ""
    ).toLowerCase();
    if (configuredProvider === "cashfree") return "cashfree";
    if (configuredProvider === "razorpay") return "razorpay";
    if (businessDetails.cashfree_vendor_id) return "cashfree";
    return "razorpay";
  };

  const validateForm = () => {
    if (!formData.customerName.trim()) return "Customer Name is required";
    if (!formData.contact.trim()) return "Contact Number is required";
    if (!/^\d+$/.test(formData.contact)) return "Contact must be a valid number";
    if (!formData.amount.trim()) return "Amount is required";
    if (Number(formData.amount) <= 0) return "Amount must be greater than 0";
    if (!formData.description.trim()) return "Description is required";
    return "";
  };

  const generateLink = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    const { baseAmount } = calculateFinalPrice();

    if (isCash && baseAmount) {
      try {
        setError("");
        const res = await addManualPaymentAPI({
          customer_name: formData.customerName,
          contact_details: formData.contact,
          description: formData.description,
          amount: baseAmount,
          payment_method: "cash",
          booking_id: booking_data.id.toString(),
        });
        if (res && res.status) {
          showSuccessToast("Payment added successfully.");
          trackClarityEvent(CLARITY_EVENTS.PACKAGE_BOOKING_MANUAL_PAYMENT_ADDED, {
            booking_id: booking_data.id,
            amount: baseAmount,
            payment_method: "cash",
            customer_name: formData.customerName,
          });
          refreshData();
          onClose();
        }
      } catch {
        showErrorToast("Error in add payment log");
      } finally {
        setLoading(false);
      }
      return;
    }

    setError("");
    try {
      const paymentProvider = resolvePaymentProvider();
      const returnUrl = buildBookingReturnUrl(getWhiteLableObject().domain);
      const res = await GenerateBookPackagePaymentAPI({
        customer_name: formData.customerName,
        contact_details: formData.contact,
        description: formData.description,
        amount: Number(customerPays.toFixed(2)),
        booking_id: booking_data.id.toString(),
        payment_provider: paymentProvider,
        return_url: returnUrl,
      });
      const responsePayload = extractBookingPaymentPayload(res);
      const generatedLink =
        responsePayload?.short_url ||
        responsePayload?.payment_link ||
        responsePayload?.payment_link_url ||
        responsePayload?.link_url;
      if (generatedLink) {
        setPaymentLink(generatedLink);
        trackClarityEvent(CLARITY_EVENTS.PACKAGE_BOOKING_PAYMENT_LINK_GENERATED, {
          booking_id: booking_data.id,
          amount: Number(customerPays.toFixed(2)),
          customer_name: formData.customerName,
          payment_mode: formData.paymentMode,
          payment_provider: responsePayload?.payment_provider || paymentProvider,
        });
      } else {
        setError("Payment link was not returned by server.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to generate payment link. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (paymentLink) {
      navigator.clipboard.writeText(paymentLink);
      showSuccessToast("Link copied to clipboard!");
    }
  };

  const shareOnWhatsApp = () => {
    if (paymentLink) {
      const message = `Pay ₹${customerPays.toFixed(2)} to ${user?.name}. Click here: ${paymentLink}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={onClose}>
        <ModalShell style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={11} />
                  Booking payment
                </HeroBadge>
                <ModalTitle>Request Payment</ModalTitle>
                <ModalSubtitle>
                  Generate a payment link or record a cash payment for this booking.
                </ModalSubtitle>
              </HeroText>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            {error && <ErrorSpan style={{ display: "block", marginBottom: 12 }}>{error}</ErrorSpan>}

            {!paymentLink ? (
              <>
                <Field>
                  <Label htmlFor="customerName">
                    <FormLabelIcon icon={User} />
                    Customer Name
                  </Label>
                  <Input
                    id="customerName"
                    name="customerName"
                    placeholder="Customer name"
                    value={formData.customerName}
                    onChange={handleChange}
                  />
                </Field>

                <Field>
                  <Label htmlFor="contact">
                    <FormLabelIcon icon={Phone} />
                    Contact Number
                  </Label>
                  <Input
                    id="contact"
                    name="contact"
                    type="number"
                    placeholder="Contact number"
                    value={formData.contact}
                    maxLength={10}
                    onChange={(e) => {
                      if (e.target.value.length <= 10) handleChange(e);
                    }}
                  />
                </Field>

                <Field>
                  <Label htmlFor="amount">
                    <FormLabelIcon icon={IndianRupee} />
                    Amount (₹)
                  </Label>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    placeholder="Amount"
                    value={formData.amount}
                    onChange={(e) => {
                      if (e.target.value.length <= 6) handleChange(e);
                    }}
                  />
                </Field>

                <Field>
                  <Label htmlFor="description">
                    <FormLabelIcon icon={FileText} />
                    Description
                  </Label>
                  <TextArea
                    id="description"
                    name="description"
                    placeholder="Payment description"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Field>

                <Field>
                  <Label>Payment Method</Label>
                  <ModeGrid>
                    <ModeCard $active={!isCash}>
                      <input
                        type="radio"
                        name="paymentMode"
                        value="upi"
                        checked={!isCash}
                        onChange={() => handlePaymentModeChange("upi")}
                      />
                      <ModeIcon $active={!isCash}>
                        <CreditCard size={20} />
                      </ModeIcon>
                      <ModeLabel $active={!isCash}>Payment Link</ModeLabel>
                    </ModeCard>
                    <ModeCard $active={isCash}>
                      <input
                        type="radio"
                        name="paymentMode"
                        value="cash"
                        checked={isCash}
                        onChange={() => handlePaymentModeChange("cash")}
                      />
                      <ModeIcon $active={isCash}>
                        <Banknote size={20} />
                      </ModeIcon>
                      <ModeLabel $active={isCash}>Cash</ModeLabel>
                    </ModeCard>
                  </ModeGrid>
                </Field>

                {!isCash ? (
                  <FeeCard>
                    <span>Platform Fee: ₹{PLATFORM_FEE}</span>
                    <FeeTotal>Customer Pays: ₹{customerPays.toFixed(2)}</FeeTotal>
                  </FeeCard>
                ) : (
                  <FeeCard>
                    <FeeTotal>Amount: ₹{Number(formData.amount) || 0}</FeeTotal>
                    <FeeHint>No additional fees for cash payments</FeeHint>
                  </FeeCard>
                )}

                <PrimaryBtn type="button" onClick={generateLink} disabled={loading}>
                  {loading ? <Spinner size={16} /> : <Link2 size={16} />}
                  {loading
                    ? "Processing..."
                    : isCash
                    ? "Record Cash Payment"
                    : "Generate Link"}
                </PrimaryBtn>
              </>
            ) : (
              <>
                <Label>
                  <FormLabelIcon icon={Link2} />
                  Payment Link
                </Label>
                <LinkBox>{paymentLink}</LinkBox>
                <ActionRow>
                  <SecondaryBtn type="button" onClick={copyLink}>
                    <Copy size={15} /> Copy
                  </SecondaryBtn>
                  <SecondaryBtn type="button" onClick={() => setShowQR(!showQR)}>
                    <QrCode size={15} /> {showQR ? "Hide QR" : "Show QR"}
                  </SecondaryBtn>
                  <WhatsAppBtn type="button" onClick={shareOnWhatsApp}>
                    <MessageCircle size={15} /> WhatsApp
                  </WhatsAppBtn>
                </ActionRow>
                {showQR && paymentLink && (
                  <QrWrap>
                    <QRCodeCanvas value={paymentLink} size={200} />
                  </QrWrap>
                )}
              </>
            )}
          </ModalBody>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
};

export default GenerateBookPackagePaymentModal;
